import { useQuery } from "@tanstack/react-query";
import api from "./api";
import { queryClient } from "./queryClient";
import { invalidateUsers } from "./queryKeys";

async function getUser() {
  try {
    var response = await api.get(
      "api/users/me"
      , { bypassInterceptor: true});
    return response.data;;
  } catch (error) {
    return false;
  }
}

async function putUserPreferences(preferencesJson) {
  try {
    var response = await api.put(
      "api/users/me/preferences",
      {
        preferencesJson
      }
      , { bypassInterceptor: true });
    return response.data;;
  } catch (error) {
    return false;
  }
}

async function getUsersPreferences() {
  try {
    var response = await api.get("api/users/me/preferences");
    return response.data;;
  } catch (error) {
    return false;
  }
}

async function getUserById(id) {
  try {
    var response = await api.get(`api/users/${id}`);
    return response.data;;
  } catch (error) {
    return false;
  }
}

async function deleteUser(id) {
  try {
    var response = await api.delete(`api/users/${id}`);
    invalidateUsers(queryClient);
    return response;
  } catch (error) {
    return false;
  }
}

async function createUser(
  name,
  email,
  branchId,
  roleId) {
  try {
    var response = await api.post(
      "api/users",
      {
        name,
        email,
        branchId,
        roleId
      });

    invalidateUsers(queryClient);
    return response.data;;
  } catch (error) {
    if (error.response.status === 409) {
      return {
        error: "duplicate"
      }
    }

    return false;
  }
}

async function updateUser(
  id,
  name,
  branchId,
  roleId) {
  try {
    var response = await api.put(
      `api/users/${id}`,
      {
        name,
        branchId,
        roleId
      });
    invalidateUsers(queryClient);
    return response.data;;
  } catch (error) {
    return false;
  }
}

// Let failures propagate so react-query keeps the query in an error state and refetches on the next
// mount, rather than caching `false` as a successful (60s-fresh) result — a request that raced ahead
// of the merchant context and 403'd would otherwise poison the shared ["users"] cache for everyone.
async function getUsers() {
  const response = await api.get("api/users");
  return response.data;
}

async function getUserSubs() {
  try {
    var response = await api.get("api/subscribers/me");
    return response.data;;
  } catch (error) {
    return false;
  }
}

async function updateSubscription(id, type, active, destination = "Email") {
  try {
    var response = await api.put(
      `api/subscribers/${id}/subscriptions`,
      {
        type,
        active,
        destination
      });
    return response.data;;
  } catch (error) {
    return false;
  }
}

const usersQuery = {
  queryKey: ["users"],
  queryFn: getUsers,
  staleTime: 60_000,
};

const asArray = (data) => (Array.isArray(data) ? data : []);

// The merchant's own people. Ontik support users are served in the same payload so their
// names can be resolved, but they are not members of the merchant's team and are filtered
// out here so every dropdown, picker and settings list keeps the shape it always had.
function useUsers() {
  return useQuery({
    ...usersQuery,
    select: (data) => asArray(data).filter((u) => !u.isSupportUser),
  });
}

// Same request and cache entry as useUsers, support users included. For putting a name to
// an action, not for listing people — see useUserName.
function useAllUsers() {
  return useQuery({
    ...usersQuery,
    select: asArray,
  });
}

export {
  getUser,
  getUserById,
  createUser,
  updateUser,
  getUsers,
  useUsers,
  useAllUsers,
  getUserSubs,
  updateSubscription,
  putUserPreferences,
  getUsersPreferences,
  deleteUser
};